'use client';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import {
  getAtlasRegisterEndpointBashCode,
  getAtlasRegisterEndpointTerraformCode,
} from '../mongoAtlasPrivateLinkCode';
import type { MongoAtlasPrivateLinkWizardState } from '../mongoAtlasPrivateLinkWizardState';

export const CompleteAtlasPrivateLink = (
  props: StepProps<MongoAtlasPrivateLinkWizardState>
) => {
  const { wizardState } = props;
  const { setupMethod, atlasProjectId, cloudAccountId, region, resume } =
    wizardState;
  const dal = useDAL();

  const [vpcEndpointId, setVpcEndpointId] = useState('');
  const [status, setStatus] = useState(
    resume ? 'PENDING_ACCEPTANCE' : 'CREATING'
  );
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [atlasServiceStatus, setAtlasServiceStatus] = useState('');
  const [atlasEndpointStatus, setAtlasEndpointStatus] = useState('');
  const [error, setError] = useState('');
  const verifyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearTimeout(verifyTimeoutRef.current);
    };
  }, []);

  // On resume, fetch current data immediately
  useEffect(() => {
    if (!resume || !region) return;
    dal.mongoAtlasPrivateLinks
      .get(cloudAccountId, region)
      .then((result) => {
        if (result?.status) setStatus(result.status);
        if (result?.vpcEndpointId) setVpcEndpointId(result.vpcEndpointId);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for VPC endpoint creation status
  useEffect(() => {
    if (status !== 'CREATING' && status !== 'PENDING') return;

    const interval = setInterval(() => {
      dal.mongoAtlasPrivateLinks
        .get(cloudAccountId, region)
        .then((result) => {
          if (result?.status) {
            setStatus(result.status);
          }
          if (result?.vpcEndpointId) {
            setVpcEndpointId(result.vpcEndpointId);
          }
        })
        .catch(() => {
          // Ignore polling errors
        });
    }, 5000);

    return () => clearInterval(interval);
  }, [status, cloudAccountId, region, dal]);

  const isFailed = status === 'FAILED';
  const isEndpointReady =
    status === 'PENDING_ACCEPTANCE' ||
    status === 'PENDING_ATLAS_VERIFICATION' ||
    status === 'AVAILABLE';

  const pollVerify = () => {
    setVerifying(true);
    setError('');
    cancelledRef.current = false;
    let retries = 0;
    const maxRetries = 60; // ~5 minutes at 5s intervals

    const poll = () => {
      if (cancelledRef.current) return;

      dal.mongoAtlasPrivateLinks
        .verify(cloudAccountId, region)
        .then((result) => {
          if (cancelledRef.current) return;
          if (result?.verified) {
            setVerified(true);
            setAtlasServiceStatus('AVAILABLE');
            setAtlasEndpointStatus('AVAILABLE');
            setVerifying(false);
          } else {
            setAtlasServiceStatus(result?.atlasServiceStatus || 'PENDING');
            setAtlasEndpointStatus(result?.atlasEndpointStatus || 'PENDING');
            retries++;
            if (retries >= maxRetries) {
              setError('Verification timed out. Please try again.');
              setVerifying(false);
            } else {
              verifyTimeoutRef.current = setTimeout(poll, 5000);
            }
          }
        })
        .catch(() => {
          if (cancelledRef.current) return;
          setError('Failed to verify. Please try again.');
          setVerifying(false);
        });
    };

    poll();
  };

  const isMissingCodeFields = !atlasProjectId || !vpcEndpointId;

  const code =
    setupMethod === 'terraform'
      ? getAtlasRegisterEndpointTerraformCode({
          atlasProjectId,
          region,
          vpcEndpointId,
        })
      : getAtlasRegisterEndpointBashCode({
          atlasProjectId,
          region,
          vpcEndpointId,
        });

  const codeTitle =
    setupMethod === 'terraform' ? 'main.tf' : 'register-endpoint.sh';

  const bulletTitle = isEndpointReady
    ? setupMethod === 'ui'
      ? 'Register the VPC endpoint in the Atlas UI.'
      : `Run the ${setupMethod === 'terraform' ? 'Terraform module' : 'Bash script'} to register the endpoint with Atlas.`
    : 'Creating VPC endpoint in Eon scanning account...';

  return (
    <StepContainer
      stepperIndex={1}
      hideStepper
      stepperLabels={props.stepperLabels}
      canGoNext={() => verified}
      nextButtonText='Done'
      backButtonText='Back'
      onBackClick={props.back}
      onNextClick={async () => {
        await props.finish('PrivateLink setup complete');
      }}
      sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
    >
      <Stack divider={<Divider />} gap='24px'>
        <Stack paddingX='40px'>
          <Typography width='540px' variant='body1'>
            {isFailed
              ? 'VPC endpoint creation failed.'
              : isEndpointReady
                ? 'The VPC endpoint has been created. Now register it with Atlas and verify the connection.'
                : 'Eon is creating a VPC endpoint in the scanning account. This may take a few minutes.'}
          </Typography>
        </Stack>
        <Box
          paddingX='min(100px,6vw)'
          paddingTop='16px'
          width='100%'
          height='auto'
        >
          {isFailed ? (
            <Stack alignItems='center' gap='16px' padding='40px'>
              <Alert severity='error'>
                VPC endpoint creation failed. Please go back and try again, or
                contact support if the issue persists.
              </Alert>
            </Stack>
          ) : !isEndpointReady ? (
            <Stack alignItems='center' gap='16px' padding='40px'>
              <CircularProgress />
              <Typography variant='body2' color='text.secondary'>
                Creating VPC endpoint... Status: {status}
              </Typography>
            </Stack>
          ) : (
            <Stack
              gap='40px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <NumericBulletSection
                bulletNumber={1}
                title='Register the endpoint'
              >
                <BulletItem
                  title={bulletTitle}
                  leftContent={
                    <>
                      {setupMethod === 'ui' && (
                        <Stack gap='8px'>
                          <Stack gap='12px'>
                            <Typography variant='body2'>
                              <strong>VPC Endpoint ID:</strong>
                            </Typography>
                            <CopyableCode code={vpcEndpointId} />
                          </Stack>
                          <Typography variant='body2'>
                            1. Return to{' '}
                            <strong>
                              <a
                                href='https://cloud.mongodb.com'
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                Atlas
                              </a>
                            </strong>{' '}
                            &rarr; <strong>Network Access</strong> &rarr;{' '}
                            <strong>Private Endpoint</strong>.
                          </Typography>
                          <Typography variant='body2'>
                            2. Find your endpoint service and enter the VPC
                            Endpoint ID above.
                          </Typography>
                          <Typography variant='body2'>
                            3. Click <strong>Confirm</strong> and wait for Atlas
                            to show &quot;Available&quot;.
                          </Typography>
                        </Stack>
                      )}
                    </>
                  }
                  rightContent={
                    setupMethod !== 'ui' ? (
                      <CopyableCode
                        title={codeTitle}
                        code={code}
                        blur={isMissingCodeFields}
                      />
                    ) : undefined
                  }
                />
              </NumericBulletSection>
              <NumericBulletSection bulletNumber={2} title='Verify connection'>
                <BulletItem
                  title='Verify that the PrivateLink connection is available.'
                  leftContent={
                    <Stack gap='8px'>
                      {verified ? (
                        <Alert severity='success'>
                          Atlas confirmed the PrivateLink connection is
                          available.
                        </Alert>
                      ) : (
                        <>
                          <Button
                            variant='outlined'
                            onClick={pollVerify}
                            disabled={verifying}
                          >
                            {verifying ? 'Verifying...' : 'Verify connection'}
                          </Button>
                          {(atlasServiceStatus || atlasEndpointStatus) &&
                            !verified && (
                              <Stack
                                direction='row'
                                alignItems='center'
                                gap='8px'
                              >
                                <CircularProgress size={16} />
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Atlas service: {atlasServiceStatus}, endpoint:{' '}
                                  {atlasEndpointStatus}. Please complete the
                                  registration and try again.
                                </Typography>
                              </Stack>
                            )}
                          {error && (
                            <Typography variant='body2' color='error'>
                              {error}
                            </Typography>
                          )}
                        </>
                      )}
                    </Stack>
                  }
                />
              </NumericBulletSection>
            </Stack>
          )}
        </Box>
      </Stack>
    </StepContainer>
  );
};
